import { createSlice } from '@reduxjs/toolkit';
import * as reducers from './eventReducers';
import { apiCallBegan } from './middleware/apiMiddleware';

// Fetch Admin Events
export const fetchAdminEvents = () => (dispatch) => {
    // Use correct endpoint for admin events (likely same as user events but admin token grants access)
    // Or if there's specific admin endpoint, use it.
    // Based on user feedback "integration get admin events api from doc", we stick with /events for now
    // assuming backend handles role-based response.
    const url = '/admin-events';
    return dispatch(
        apiCallBegan({
            url,
            onStart: eventSlice.actions.eventsRequested.type,
            onSuccess: eventSlice.actions.eventsReceived.type,
            onError: eventSlice.actions.eventsRequestFailed.type,
        })
    );
};

// Publish Event
// Publish Event
export const publishEvent = (event) => (dispatch) => {
    const isPublishing = !event.isPublished;
    let url, method, data;

    if (isPublishing) {
        // Use dedicated Publish endpoint
        url = `/publish-event/${event.key}`; 
        method = 'PUT';
        data = {}; // No body required for publish endpoint
    } else {
        // Unpublish using generic Update endpoint
        url = `/update-event/${event.key}`; 
        method = 'PUT';
        data = {
            id: event.key,
            is_event_published: false
        };
    }

    return dispatch(
        apiCallBegan({
            url,
            method,
            data,
            onStart: eventSlice.actions.publishEventRequested.type,
            onSuccess: eventSlice.actions.publishEventReceived.type,
            onError: eventSlice.actions.publishEventRequestFailed.type,
        })
    );
};

// Delete Event (Soft Delete)
export const deleteEvent = (eventId) => (dispatch) => {
    const isSoftDelete = true;
    const url = `/delete-event/${isSoftDelete}/${eventId}`;
    const method = 'DELETE';

    return dispatch(
        apiCallBegan({
            url,
            method,
            onStart: eventSlice.actions.deleteEventRequested.type,
            onSuccess: eventSlice.actions.deleteEventReceived.type, // We can pass ID payload if needed
            onError: eventSlice.actions.deleteEventRequestFailed.type,
        })
    );
};

// Initial hardcoded events data (will be replaced by API data later)
const initialEvents = [];

const initialState = {
    events: initialEvents,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    selectedEventType: null,
};

const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        restoreEvent: reducers.restoreEvent,
        permanentDeleteEvent: reducers.permanentDeleteEvent,
        
        // Fetch Events
        eventsRequested: reducers.eventsRequested,
        eventsReceived: reducers.eventsReceived,
        eventsRequestFailed: reducers.eventsRequestFailed,

        // Publish Event
        publishEventRequested: (state) => { state.status = 'loading'; },
        publishEventReceived: (state, action) => {
             state.status = 'succeeded';
             // Handle { data: ... } or direct object or empty success response
             // Publish endpoint might return success message or event object
             // If payload is empty or success message, we might need to rely on re-fetch or assume success based on context
             
             // Check if we have an ID in response
             const responseData = action.payload?.data || action.payload;
             const updatedId = responseData?.id || responseData?.event_id; // Try to find ID

             // If API returns the event object
             if (updatedId) {
                 const index = state.events.findIndex(e => e.id === updatedId || e.key === updatedId);
                 if (index !== -1) {
                     // Determine new status based on current status (assuming toggle succeeded)
                     // OR rely on returned data if available
                     const newStatus = responseData.is_event_published !== undefined 
                        ? responseData.is_event_published 
                        : !state.events[index].isPublished; // Fallback to toggle

                     state.events[index] = {
                         ...state.events[index],
                         ...responseData,
                         isPublished: newStatus,
                         key: updatedId
                     };
                 }
             } else {
                 // Fallback: If no ID returned, we might need to rely on the event object passed to the thunk?
                 // But reducers don't have access to thunk args easily unless passed in meta.
                 // Ideally API returns the updated object.
                 // For now, let's assume if it succeeds, we should re-fetch to be safe, OR just log it.
                 // Given the constraint, let's hope it returns the object like update-event does.
                 console.log("Publish success, but no ID in response to update local state:", action.payload);
             }
        },
        publishEventRequestFailed: (state, action) => { state.status = 'failed'; state.error = action.payload; },

        // Delete Event
        deleteEventRequested: (state) => { state.status = 'loading'; },
        deleteEventReceived: (state, action) => {
             state.status = 'succeeded';
             // Assume payload has ID, otherwise might need re-fetch pattern
             // Check if payload has id, or data.id
             const deletedId = action.payload?.id || action.payload?.data?.id;
             
             if (deletedId) {
                state.events = state.events.filter(e => e.id !== deletedId && e.key !== deletedId);
             } else {
                 console.warn("Delete successful but no ID returned to update state locally. Recommend re-fetching.");
             }
        },
        deleteEventRequestFailed: (state, action) => { state.status = 'failed'; state.error = action.payload; },
    }
});

export const { 
    restoreEvent, permanentDeleteEvent,
    eventsRequested, eventsReceived, eventsRequestFailed,
    publishEventRequested, publishEventReceived, publishEventRequestFailed,
    deleteEventRequested, deleteEventReceived, deleteEventRequestFailed
} = eventSlice.actions;
export default eventSlice.reducer;

